import { Box, Card, CardActionArea, CardContent, CardMedia, Chip, Typography } from "@mui/material";
import { PlayCircleOutline, ErrorOutline } from "@mui/icons-material";
import { PastStream } from "../../_types/stream-status.type";

interface PastStreamCardProps {
    stream: PastStream;
    onClick: () => void;
}

function formatDuration(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
        return `${hrs}h ${mins}m`;
    }
    if (mins > 0) {
        return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
}

function formatTime(dateString: string): string {
    return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
}

function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

export default function PastStreamCard({ stream, onClick }: PastStreamCardProps) {
    const isProcessing = stream.status === 'processing' || stream.status === 'pending';
    const isFailed = stream.status === 'failed';
    const isClickable = !isProcessing && !isFailed;

    return (
        <Card
            sx={{
                width: '100%',
                maxWidth: 320,
                opacity: isProcessing ? 0.7 : 1,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': isClickable ? {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                } : {}
            }}
        >
            <CardActionArea
                onClick={isClickable ? onClick : undefined}
                disabled={!isClickable}
                sx={{ height: '100%' }}
            >
                {/* Thumbnail with overlay */}
                <Box sx={{ position: 'relative' }}>
                    {stream.thumbnailUrl ? (
                        <CardMedia
                            component="img"
                            height="180"
                            image={stream.thumbnailUrl}
                            alt={`Stream from ${formatDate(stream.start)}`}
                            sx={{ objectFit: 'cover' }}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: 180,
                                bgcolor: 'grey.900',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography variant="caption" color="grey.500">
                                No thumbnail
                            </Typography>
                        </Box>
                    )}

                    {/* Overlay */}
                    {!isProcessing && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(0,0,0,0.3)',
                                transition: 'background-color 0.2s',
                                '&:hover': isClickable ? {
                                    bgcolor: 'rgba(0,0,0,0.5)'
                                } : {}
                            }}
                        >
                            {isFailed ? (
                                <ErrorOutline sx={{ fontSize: 56, color: 'error.main' }} />
                            ) : (
                                <PlayCircleOutline sx={{ fontSize: 64, color: 'white', opacity: 0.9 }} />
                            )}
                        </Box>
                    )}

                    {/* Duration badge - show for completed and failed */}
                    {!isProcessing && (
                        <Chip
                            label={isFailed ? 'Failed' : formatDuration(stream.duration)}
                            size="small"
                            color={isFailed ? 'error' : undefined}
                            sx={{
                                position: 'absolute',
                                bottom: 8,
                                right: 8,
                                bgcolor: isFailed ? undefined : 'rgba(0,0,0,0.75)',
                                color: isFailed ? undefined : 'white',
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                            }}
                        />
                    )}
                </Box>

                {/* Content */}
                <CardContent sx={{ pb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {formatDate(stream.start)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, color: 'text.secondary', mb: isProcessing ? 1.5 : 0 }}>
                        <Box>
                            <Typography variant="caption" display="block" color="text.disabled">
                                Started
                            </Typography>
                            <Typography variant="body2">
                                {formatTime(stream.start)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" display="block" color="text.disabled">
                                Ended
                            </Typography>
                            <Typography variant="body2">
                                {formatTime(stream.stop)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" display="block" color="text.disabled">
                                Duration
                            </Typography>
                            <Typography variant="body2">
                                {formatDuration(stream.duration)}
                            </Typography>
                        </Box>
                    </Box>
                    {isProcessing && (
                        <Typography variant="caption" color="warning.main">
                            Recording is being prepared. Check back shortly.
                        </Typography>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
}
